function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { hasTracingEnabled, getCurrentHub, getDynamicSamplingContextFromClient } from '@sentry/core';
import { generateSentryTraceHeader, dynamicSamplingContextToSentryBaggageHeader, isInstanceOf, BAGGAGE_HEADER_NAME } from '@sentry/utils';
/**
 * Create and track fetch request spans for usage in combination with `addInstrumentationHandler`.
 *
 * @returns Span if a span was created, otherwise void.
 */

function instrumentFetchRequest(handlerData, shouldCreateSpan, shouldAttachHeaders, spans) {
  var spanOrigin = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 'auto.http.browser';

  if (!hasTracingEnabled() || !handlerData.fetchData) {
    return undefined;
  }

  var shouldCreateSpanResult = shouldCreateSpan(handlerData.fetchData.url);

  if (handlerData.endTimestamp && shouldCreateSpanResult) {
    var spanId = handlerData.fetchData.__span;
    if (!spanId) return;
    var _span = spans[spanId];

    if (_span) {
      if (handlerData.response) {
        _span.setHttpStatus(handlerData.response.status);

        var contentLength = handlerData.response && handlerData.response.headers && handlerData.response.headers.get('content-length');

        if (contentLength) {
          var contentLengthNum = parseInt(contentLength);

          if (contentLengthNum > 0) {
            _span.setData('http.response_content_length', contentLengthNum);
          }
        }
      } else if (handlerData.error) {
        _span.setStatus('internal_error');
      }

      _span.finish(); // eslint-disable-next-line @typescript-eslint/no-dynamic-delete


      delete spans[spanId];
    }

    return undefined;
  }

  var hub = getCurrentHub();
  var scope = hub.getScope();
  var client = hub.getClient();
  var parentSpan = scope.getSpan();
  var _handlerData$fetchDat = handlerData.fetchData,
      method = _handlerData$fetchDat.method,
      url = _handlerData$fetchDat.url;
  var span = shouldCreateSpanResult && parentSpan ? parentSpan.startChild({
    data: {
      url: url,
      type: 'fetch',
      'http.method': method
    },
    description: "".concat(method, " ").concat(url),
    op: 'http.client',
    origin: spanOrigin
  }) : undefined;

  if (span) {
    handlerData.fetchData.__span = span.spanId;
    spans[span.spanId] = span;
  }

  if (shouldAttachHeaders(handlerData.fetchData.url) && client) {
    var request = handlerData.args[0]; // In case the user hasn't set the second argument of a fetch call we default it to `{}`.

    handlerData.args[1] = handlerData.args[1] || {}; // eslint-disable-next-line @typescript-eslint/no-explicit-any

    var options = handlerData.args[1]; // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access

    options.headers = addTracingHeadersToFetchRequest(request, client, scope, options, span);
  }

  return span;
}
/**
 * Adds sentry-trace and baggage headers to the various forms of fetch headers
 */


function addTracingHeadersToFetchRequest(request, // unknown is actually type Request but we can't export DOM types from this package,
client, scope, options, requestSpan) {
  var span = requestSpan || scope.getSpan();
  var transaction = span && span.transaction;

  var _scope$getPropagation = scope.getPropagationContext(),
      traceId = _scope$getPropagation.traceId,
      sampled = _scope$getPropagation.sampled,
      dsc = _scope$getPropagation.dsc;

  var sentryTraceHeader = span ? span.toTraceparent() : generateSentryTraceHeader(traceId, undefined, sampled);
  var dynamicSamplingContext = transaction ? transaction.getDynamicSamplingContext() : dsc ? dsc : getDynamicSamplingContextFromClient(traceId, client, scope);
  var sentryBaggageHeader = dynamicSamplingContextToSentryBaggageHeader(dynamicSamplingContext);
  var headers = typeof Request !== 'undefined' && isInstanceOf(request, Request) ? request.headers : options.headers;

  if (!headers) {
    return {
      'sentry-trace': sentryTraceHeader,
      baggage: sentryBaggageHeader
    };
  } else if (typeof Headers !== 'undefined' && isInstanceOf(headers, Headers)) {
    var newHeaders = new Headers(headers);
    newHeaders.append('sentry-trace', sentryTraceHeader);

    if (sentryBaggageHeader) {
      // If the same header is appended multiple times the browser will merge the values into a single request header.
      // Its therefore safe to simply push a "baggage" entry, even though there might already be another baggage header.
      newHeaders.append(BAGGAGE_HEADER_NAME, sentryBaggageHeader);
    }

    return newHeaders;
  } else if (Array.isArray(headers)) {
    var _newHeaders = [].concat(_toConsumableArray(headers), [['sentry-trace', sentryTraceHeader]]);

    if (sentryBaggageHeader) {
      // If there are multiple entries with the same key, the browser will merge the values into a single request header.
      // Its therefore safe to simply push a "baggage" entry, even though there might already be another baggage header.
      _newHeaders.push([BAGGAGE_HEADER_NAME, sentryBaggageHeader]);
    }

    return _newHeaders;
  } else {
    var existingBaggageHeader = 'baggage' in headers ? headers.baggage : undefined;
    var newBaggageHeaders = [];

    if (Array.isArray(existingBaggageHeader)) {
      newBaggageHeaders.push.apply(newBaggageHeaders, _toConsumableArray(existingBaggageHeader));
    } else if (existingBaggageHeader) {
      newBaggageHeaders.push(existingBaggageHeader);
    }

    if (sentryBaggageHeader) {
      newBaggageHeaders.push(sentryBaggageHeader);
    }

    return _objectSpread(_objectSpread({}, headers), {}, {
      'sentry-trace': sentryTraceHeader,
      baggage: newBaggageHeaders.length > 0 ? newBaggageHeaders.join(',') : undefined
    });
  }
}

export { addTracingHeadersToFetchRequest, instrumentFetchRequest };